import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Switch,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { actionsKeys, permissionActions, toApiSort } from "@/lib/utils";
import { setCommunityData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetCommunities,
  useGetDocumentTypeList,
  useUpdateDocumentTypeStatusMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Edit, MoreVertical } from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";

const useDocumentTypeConfig = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "documentTypeId", desc: true }]);
  const [communityKey, setCommunityKey] = useState(Math.random() * 2);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const {communityData: communityDetail, accessPermission} = useSelector(
    (state: RootState) => state.currentData
  );
  const isMasterData = location?.pathname?.toLowerCase()?.includes("master-data");
  const communityId = isMasterData ? communityDetail?.communityId : id;

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  useEffect(() => {
    if (communityList?.data) {
      setCommunityKey(Math.random() * 3);
    }
  }, [communityList]);

  const breadcrumbData = isMasterData
    ? undefined
    : [
        {
          link: `/${routes.cam}/${routes.communities}`,
          name: `Community - ${(communityDetail as any)?.name || (communityDetail as any)?.communityName || ""}`,
        },
        {
          link: "",
          name: <span className="text-primary">{"Document Type"}</span>,
        },
      ];

  const {
    data: docTypes,
    isLoading: isDocTypeLoading,
    refetch: refetchDocType,
  } = useGetDocumentTypeList({
    data: {
      filters: [
        {
          field: "communityId",
          value: communityId,
          operator: "EQUAL",
        },
        {
          field: "deleted",
          value: false,
          operator: "EQUAL",
        },
      ],
      search: globalFilter,
      searchFields: [
        "documentType",
        "transactionalProcessor",
        "startRange",
        "updatedByUserName",
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "documentTypeId", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityId,
    },
  });

  useEffect(() => {
    refetchDocType();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter.length >= 2 || globalFilter.length == 0) {
      refetchDocType();
    }
  }, [globalFilter]);

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateDocumentTypeStatusMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["document-type"],
        });
        toast.success("Status Updated Successfully");
      },
    });

  const hasAddPermission = useMemo(() => accessPermission?.[actionsKeys?.DOCUMENT_TYPE?.toLowerCase()]?.[permissionActions.CREATE], [accessPermission]);
  const hasUpdatePermission = useMemo(() => accessPermission?.[actionsKeys?.DOCUMENT_TYPE?.toLowerCase()]?.[permissionActions.UPDATE], [accessPermission]);
  

  const onAdd = () => {
    navigate("add");
  };

  const onCommunityChange = (e: any) => {
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
    setTimeout(() => refetchDocType(), 100);
  };

  // Define Table Columns
  const columns = [
    {
      // Per-community number (starts at 1 in every community), not the table-wide id
      accessorKey: "displayId",
      header: "ID",
      accessorFn: (row: any) => String(row.displayId ?? ""),
    },
    {
      accessorKey: "documentType",
      header: (
        <div className="w-full">
          Document <br /> Type
        </div>
      ),
      accessorFn: (row: any) => String(row?.documentType || ""),
    },
    {
      accessorKey: "transactionalProcessor",
      header: (
        <div className="w-full">
          Transactional <br /> Processor
        </div>
      ),
      accessorFn: (row: any) => String(row?.transactionalProcessor || ""),
    },
    {
      accessorKey: "description",
      header: (
        <div className="w-full">
          Description
        </div>
      ),
      accessorFn: (row: any) => String(row?.description || ""),
    },
    // {
    //   accessorKey: "startRange",
    //   header: (
    //     <div className=" w-full">
    //       Start <br /> Range
    //     </div>
    //   ),
    //   accessorFn: (row: any) => String(row?.startRange || ""),
    // },
    // {
    //   accessorKey: "endRange",
    //   header: (
    //     <div className=" w-full">
    //       End <br /> Range
    //     </div>
    //   ),
    //   accessorFn: (row: any) => String(row?.endRange || ""),
    // },
    {
      accessorKey: "updatedAt",
      header: (
        <div className=" w-full">
          Last <br /> Updated On
        </div>
      ),
      accessorFn: (row: any) =>
        row?.updatedAt ? moment(row?.updatedAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "updatedByUserName",
      header: (
        <div className=" w-full">
          Last <br /> Updated By
        </div>
      ),
      accessorFn: (row: any) => String(row?.updatedByUserName || ""),
    },
    {
      accessorKey: "active",
      header: <div>Status</div>,
      cell: (row: any) => (
        <div
          className="flex items-center space-x-2"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Switch
            id="active"
            onCheckedChange={(isActive: boolean) => {
              onStatusChange(row.row.original, isActive);
            }}
            checked={row.row.original.active}
            disabled={isUpdatePending || !hasUpdatePermission}
          />
        </div>
      ),
    },
    ...(hasUpdatePermission
      ? [
          {
            id: "actions",
            header: "Actions",
            enableSorting: false,
            cell: (row: any) => {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <MoreVertical className="w-5 h-5 cursor-pointer" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-white border-0 shadow-md"
                  >
                    {hasUpdatePermission && (
                      <DropdownMenuItem
                        className="flex items-center gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          navigate(`${row.row.original.documentTypeId}/edit`);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ]
      : []),
  ];

  const onRowClick = (row: any) => {
    navigate(`${row.original.documentTypeId}/view`);
  };

  const onStatusChange = (row: any, isActive: boolean) => {
    updateInfo({
      communityId: communityId as string,
      documentTypeId: row?.documentTypeId as string,
      active: isActive,
    });
  };

  return {
    isMobile,
    globalFilter,
    setGlobalFilter,
    onAdd,
    docTypes,
    isDocTypeLoading,
    columns,
    onRowClick,
    breadcrumbData,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    hasAddPermission,
    isMasterDataRoute: isMasterData,
    communityList,
    isCommunityLoading,
    communityId,
    onCommunityChange,
    communityKey,
  };
};

export default useDocumentTypeConfig;
